import { useWindowSize } from 'react-use';
import { useFirmBranding } from '../../../../Firm';
import { useEffect, useRef, useState } from 'react';
import { useFirmClient } from '../../../../FirmClient';
import {
  ESTATE_STATUS_SCORECARD_ELEMENT_SYMBOL,
  SCORECARD_ASSETS_ELEMENT_SYMBOLS,
  SCORECARD_CASH_FLOW_ELEMENT_SYMBOLS,
  SCORECARD_ELEMENT_SYMBOLS,
  SCORECARD_RISK_ELEMENT_SYMBOLS,
  ScorecardElementSymbol,
  useScorecard,
} from '../../../../Scorecard';
import { useFeatureFlagsContext } from '../../../../FeatureFlags';
import { PresentationViewOptions } from '../types';
import { DEFAULT_PRESENTATION_VIEW_OPTIONS } from '../constants';
import { getHeadsOfHousehold, usePersons } from '../../../../Person';
import { isElementGroupFocused } from '../util';
import { useBtDetails } from '../../../../BtDetails';
import { formatEstateStatusWeight, useEstateStatusReadOnlyHouseholdSettings } from '../../../../EstateStatus';

export interface UseFullScreenScorecardPageArgs {
  firmID: string;
  clientID: string;
  householdID: string;
  pathName?: string;
  fullScreenRegex?: RegExp;
}
export const useFullScreenScorecardPage = ({
  firmID,
  clientID,
  householdID,
  pathName,
  fullScreenRegex,
}: UseFullScreenScorecardPageArgs) => {
  const { width, height } = useWindowSize();
  const { allowEstateStatus } = useFeatureFlagsContext();

  const { firmClient } = useFirmClient({ firmClientID: clientID, firmID });
  const { scorecard, scorecardQuery } = useScorecard({ householdID });
  const { estateStatusHouseholdSettings } = useEstateStatusReadOnlyHouseholdSettings(
    allowEstateStatus ? householdID : undefined
  );
  const { btDetails } = useBtDetails({ householdID, includeBusinesses: true });
  const { firmBranding, firmBrandingQuery } = useFirmBranding(firmID);
  const { persons } = usePersons({ householdID });

  const firmHasNoLogo = !firmBrandingQuery.isLoading && !firmBranding?.logo;
  const headsOfHousehold = getHeadsOfHousehold(persons);

  const scorecardRef = useRef<HTMLDivElement>(null);
  const firmLogoImageRef = useRef<HTMLImageElement>(null);
  const fullPresentationRef = useRef<HTMLDivElement>(null);
  const [scaleFactor, setScaleFactor] = useState<number>();
  const scorecardElementSymbolsWithoutEstateStatus = SCORECARD_ELEMENT_SYMBOLS.filter(
    (element) => element !== ESTATE_STATUS_SCORECARD_ELEMENT_SYMBOL
  );
  const scorecardElementSymbols =
    allowEstateStatus === true ? SCORECARD_ELEMENT_SYMBOLS : scorecardElementSymbolsWithoutEstateStatus;
  const riskElementSymbols =
    allowEstateStatus === true
      ? SCORECARD_RISK_ELEMENT_SYMBOLS
      : SCORECARD_RISK_ELEMENT_SYMBOLS.filter((element) => element !== ESTATE_STATUS_SCORECARD_ELEMENT_SYMBOL);
  const [viewOptions, setViewOptions] = useState<PresentationViewOptions>(() => ({
    ...DEFAULT_PRESENTATION_VIEW_OPTIONS,
    focusedElements: scorecardElementSymbols,
  }));

  useEffect(() => {
    setViewOptions((prev) => {
      const focusedElements = prev.focusedElements ?? [];
      const includesEstateStatus = focusedElements.includes(ESTATE_STATUS_SCORECARD_ELEMENT_SYMBOL);

      if (!allowEstateStatus && includesEstateStatus) {
        return {
          ...prev,
          focusedElements: focusedElements.filter((element) => element !== ESTATE_STATUS_SCORECARD_ELEMENT_SYMBOL),
        };
      }

      const focusedDefaultElements =
        focusedElements.length === scorecardElementSymbolsWithoutEstateStatus.length &&
        scorecardElementSymbolsWithoutEstateStatus.every((element) => focusedElements.includes(element));

      if (allowEstateStatus === true && !includesEstateStatus && focusedDefaultElements) {
        return {
          ...prev,
          focusedElements: [...focusedElements, ESTATE_STATUS_SCORECARD_ELEMENT_SYMBOL],
        };
      }

      return prev;
    });
  }, [allowEstateStatus]);

  const allFocused = viewOptions.focusedElements?.length === scorecardElementSymbols.length;
  const cashFlowFocused = isElementGroupFocused(viewOptions.focusedElements, SCORECARD_CASH_FLOW_ELEMENT_SYMBOLS);
  const assetsFocused = isElementGroupFocused(viewOptions.focusedElements, SCORECARD_ASSETS_ELEMENT_SYMBOLS);
  const riskFocused = isElementGroupFocused(viewOptions.focusedElements, riskElementSymbols);
  const [shortcutsEnabled, setShortcutsEnabled] = useState(true);
  const estateStatusChecklistItems = estateStatusHouseholdSettings?.checklist?.map((item) => ({
    id: item.id,
    label: item.label,
    state: item.state,
    weightLabel: formatEstateStatusWeight(item.weight),
  }));

  useEffect(() => {
    if (fullScreenRegex && pathName) {
      setShortcutsEnabled(fullScreenRegex.test(pathName));
    }
  }, [pathName, fullScreenRegex]);

  function modifyViewOptions(options: Partial<PresentationViewOptions>) {
    setViewOptions((prev) => ({ ...prev, ...options }));
  }

  function toggleShowEmojis() {
    modifyViewOptions({ showEmojis: !viewOptions.showEmojis });
  }

  function toggleShowTargetScores() {
    modifyViewOptions({ showTargetScores: !viewOptions.showTargetScores });
  }

  function toggleShowClientName() {
    modifyViewOptions({ showClientName: !viewOptions.showClientName });
  }

  function toggleShowClientAge() {
    modifyViewOptions({ showClientAge: !viewOptions.showClientAge });
  }

  function toggleShowFinancialDetails() {
    modifyViewOptions({
      showFinancialDetails: !viewOptions.showFinancialDetails,
    });
  }

  function toggleElementVisibility(element: ScorecardElementSymbol) {
    modifyViewOptions({
      focusedElements: viewOptions.focusedElements?.includes(element)
        ? viewOptions.focusedElements.filter((e) => e !== element)
        : [...(viewOptions.focusedElements ?? []), element],
    });
  }

  function focusElementVisibility(element: ScorecardElementSymbol) {
    modifyViewOptions({
      focusedElements:
        viewOptions.focusedElements?.includes(element) && viewOptions.focusedElements.length === 1 ? [] : [element],
    });
  }

  function toggleAllElementsVisibility() {
    modifyViewOptions({
      focusedElements: allFocused ? [] : scorecardElementSymbols,
    });
  }

  function toggleCashFlowElementsVisibility() {
    modifyViewOptions({
      focusedElements: cashFlowFocused ? scorecardElementSymbols : SCORECARD_CASH_FLOW_ELEMENT_SYMBOLS,
    });
  }

  function toggleAssetsElementsVisibility() {
    modifyViewOptions({
      focusedElements: assetsFocused ? scorecardElementSymbols : SCORECARD_ASSETS_ELEMENT_SYMBOLS,
    });
  }

  function toggleRiskElementsVisibility() {
    modifyViewOptions({
      focusedElements: riskFocused ? scorecardElementSymbols : riskElementSymbols,
    });
  }

  useEffect(() => {
    setScaleFactor(Math.min(width / 1920, height / 1080));
  }, [width, height]);

  return {
    btDetails,
    householdID,
    firmHasNoLogo,
    scorecard,
    scorecardQuery,
    scaleFactor,
    firmBranding,
    firmBrandingQuery,
    fullPresentationRef,
    scorecardRef,
    firmLogoImageRef,
    firmClient,
    viewOptions,
    headsOfHousehold,
    toggleShowClientName,
    toggleShowClientAge,
    toggleElementVisibility,
    toggleAllElementsVisibility,
    toggleCashFlowElementsVisibility,
    toggleAssetsElementsVisibility,
    toggleRiskElementsVisibility,
    allFocused,
    cashFlowFocused,
    riskFocused,
    assetsFocused,
    toggleShowFinancialDetails,
    toggleShowEmojis,
    toggleShowTargetScores,
    focusElementVisibility,
    shortcutsEnabled,
    allowEstateStatus,
    estateStatusChecklistItems,
  };
};
